import { apiClient } from './config.js';

export type SocialPlatform = 'twitter' | 'linkedin';
export type SocialStatus = 'draft' | 'posted' | 'skipped' | 'approved' | 'failed';

export interface SocialDraft {
  id: number;
  article_id: string;
  target_id: string;
  platform: SocialPlatform;
  status: SocialStatus;
  composed_text: string;
  image_url: string | null;
  live_url: string;
  platform_post_url: string | null;
  posted_by: string | null;
  posted_at: string | null;
  skipped_at: string | null;
  created_at: string;
  // Buffer integration (migration 199).
  shortlisted_at?: string | null;
  shortlist_rank?: number | null;
  buffer_post_id?: string | null;
  buffer_channel_id?: string | null;
  pushed_to_buffer_at?: string | null;
  push_attempts?: number;
  last_error?: string | null;
  // Joined fields (listDrafts only)
  article_title?: string;
  company_code?: string | null;
  target_name?: string;
  target_renderer?: string | null;
  /** Sales-relationship badges: coverage | campaign | prospect | major. */
  coverage_tiers?: string[];
}

export interface ListDraftsResponse {
  success: boolean;
  count: number;
  drafts: SocialDraft[];
  timestamp: string;
}

export interface ListDraftsParams {
  status?: SocialStatus | 'all';
  platform?: SocialPlatform;
  targetId?: string;
  articleId?: string;
  /** YYYY-MM-DD — drafts created on this date. */
  date?: string;
  /** Free-text search across company_code + article_title. */
  q?: string;
  /** Entity-group filter pill — see EntityGroupFilter.tsx. */
  entity_group?: 'coverage' | 'prospects' | 'majors' | 'picked' | 'all' | 'campaigns';
  limit?: number;
  offset?: number;
}

export async function listSocialDrafts(params: ListDraftsParams = {}): Promise<ListDraftsResponse> {
  const response = await apiClient.get<ListDraftsResponse>('/admin/social/drafts', { params });
  return response.data;
}

export async function editSocialDraft(id: number, composedText: string): Promise<{ success: boolean; draft: SocialDraft }> {
  const response = await apiClient.patch<{ success: boolean; draft: SocialDraft }>(
    `/admin/social/drafts/${id}`,
    { composed_text: composedText },
  );
  return response.data;
}

export async function getSocialDraft(id: number): Promise<{ success: boolean; draft: SocialDraft }> {
  const response = await apiClient.get<{ success: boolean; draft: SocialDraft }>(`/admin/social/drafts/${id}`);
  return response.data;
}

export async function markPosted(id: number, platformPostUrl?: string): Promise<{ success: boolean; draft: SocialDraft }> {
  const response = await apiClient.post<{ success: boolean; draft: SocialDraft }>(
    `/admin/social/drafts/${id}/mark-posted`,
    { platform_post_url: platformPostUrl ?? null },
  );
  return response.data;
}

export async function skipSocialDraft(id: number): Promise<{ success: boolean; draft: SocialDraft }> {
  const response = await apiClient.post<{ success: boolean; draft: SocialDraft }>(`/admin/social/drafts/${id}/skip`);
  return response.data;
}

export async function regenerateSocialDraft(id: number): Promise<{ success: boolean; draft: SocialDraft }> {
  const response = await apiClient.post<{ success: boolean; draft: SocialDraft }>(`/admin/social/drafts/${id}/regenerate`);
  return response.data;
}

export interface PushToBufferResponse {
  success: boolean;
  result: 'success' | 'failed' | 'queued';
  message?: string;
  draft: SocialDraft | null;
}

/** Push a single LinkedIn draft to Buffer (draft → approved, or queued/failed). */
export async function pushToBuffer(id: number): Promise<PushToBufferResponse> {
  const response = await apiClient.post<PushToBufferResponse>(
    `/admin/social/drafts/${id}/push-to-buffer`,
  );
  return response.data;
}

export interface BulkPushResponse {
  success: boolean;
  summary: Record<string, number>;
  results: Array<{ id: number; result: string; reason?: string; message?: string }>;
}

/** Push a set of drafts (e.g. the confirmed shortlist) to Buffer. */
export async function bulkPushToBuffer(ids: number[]): Promise<BulkPushResponse> {
  const response = await apiClient.post<BulkPushResponse>(
    `/admin/social/drafts/push-to-buffer`,
    { ids },
  );
  return response.data;
}

export async function createAdHocSocialDraft(
  articleId: string,
  targetName: string,
  platform: SocialPlatform,
): Promise<{ success: boolean; draft: SocialDraft; already_exists?: boolean }> {
  const response = await apiClient.post<{ success: boolean; draft: SocialDraft; already_exists?: boolean }>(
    `/admin/social/drafts`,
    { article_id: articleId, target_name: targetName, platform },
  );
  return response.data;
}

// ============================================================================
// Entity social handles (manage on /admin/entities/:id/social)
// ============================================================================

export interface EntitySocialHandles {
  twitter_handle: string | null;
  linkedin_url: string | null;
  linkedin_urn: string | null;
  linkedin_page_name: string | null;
  verified: boolean;
  source: string;
  updated_at: string | null;
  linkedin_resolution_status: string | null;
  linkedin_handle_confidence: number | null;
}

export async function getEntitySocial(entityId: string): Promise<EntitySocialHandles> {
  const response = await apiClient.get<EntitySocialHandles>(`/admin/entities/${entityId}/social`);
  return response.data;
}

export async function updateEntitySocial(
  entityId: string,
  payload: Partial<EntitySocialHandles>,
): Promise<EntitySocialHandles> {
  const response = await apiClient.patch<EntitySocialHandles>(`/admin/entities/${entityId}/social`, payload);
  return response.data;
}

// ============================================================================
// LinkedIn company-tag review queue (surfaced in the Triage page)
// ============================================================================

export interface LinkedInReviewQueueItem {
  entity_id: string;
  display_name: string;
  canonical_identifier: string | null;
  linkedin_url: string | null;
  linkedin_urn: string | null;
  linkedin_page_name: string | null;
  linkedin_resolution_status: string | null;
  linkedin_resolution_attempts: number;
  /** coverage | campaign | prospect — why the row sits at the top (2026-09-21). */
  priority_group?: 'coverage' | 'campaign' | 'prospect' | null;
  social_handles_source: string | null;
}

export interface LinkedInReviewQueuePage {
  items: LinkedInReviewQueueItem[];
  /** Size of the whole queue (same predicate as `items`), not the page length. */
  total: number;
  limit: number;
  offset: number;
}

export async function listLinkedinReviewQueue(
  params: { limit?: number; offset?: number } = {},
): Promise<LinkedInReviewQueuePage> {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const response = await apiClient.get<LinkedInReviewQueuePage | LinkedInReviewQueueItem[]>(
    '/admin/entities/linkedin-review-queue',
    { params: { limit, offset } },
  );
  // A backend that predates paging returns a bare list. Wrap it so the panel
  // keeps working during a rollout where the manager ships first.
  const data = response.data;
  if (Array.isArray(data)) {
    return { items: data, total: data.length, limit, offset };
  }
  return data;
}

// ============================================================================
// Daily Buffer push cap (migration 236) — operator-managed from /social
// ============================================================================

export interface SocialPushSettings {
  daily_limit_enabled: boolean;
  daily_limit_per_site: number;
  updated_at: string | null;
  updated_by: string | null;
}

export async function getPushSettings(): Promise<SocialPushSettings> {
  const response = await apiClient.get<{ success: boolean; settings: SocialPushSettings }>(
    '/admin/social/push-settings',
  );
  return response.data.settings;
}

export async function updatePushSettings(
  payload: { daily_limit_enabled?: boolean; daily_limit_per_site?: number },
): Promise<SocialPushSettings> {
  const response = await apiClient.put<{ success: boolean; settings: SocialPushSettings }>(
    '/admin/social/push-settings',
    payload,
  );
  return response.data.settings;
}
